<!-- @component Button a basic HTML button with Urban styling-->
<!-- A generative AI model wrote or edited portions of this file with
     the supervision of a human developer and careful human review. -->
<script>
  /**
   * @typedef Props
   * @type Object
   * @property {"primary" | "primary-black" | "primary-gray" | "secondary" | "secondary-black" | "tertiary"} [variant="primary"] Which variant of button to use
   * @property {"standard" | "small"} [size="standard"]
   * @property {boolean} [disabled=false] Is the button disabled?
   * @property {(event: MouseEvent) => void} [onmouseenter=() => {}] Callback for mouse enter event
   * @property {(event: MouseEvent) => void} [onmouseleave=() => {}] Callback for mouse leave event
   * @property {(event: MouseEvent) => void} [onclick=() => {}] Callback for click event
   * @property {import('svelte').Snippet} [children=() => {}] Content rendered inside the button
   * @property {import('svelte').Snippet<[string]>} [icon=(iconColor) => {}] Optional icon snippet that receives the computed icon color
   * @property {"right" | "left"} [iconPosition="right"] whether the icon appears on the left or the right
   */

  /**
   * @type {Props}
   */
  const {
    variant = "primary",
    size = "standard",
    disabled = false,
    onmouseenter = () => {},
    onmouseleave = () => {},
    onclick = () => {},
    children = () => {},
    icon = (iconColor) => {},
    iconPosition = "right"
  } = $props();

  let el;

  let hovered = false;

  function getIconColor(_variant, _hovered, el) {
    if (!el) return;
    let iconColor = getComputedStyle(el).getPropertyValue("--button-color");
    return iconColor;
  }

  function _onmouseenter(e) {
    hovered = true;
    onmouseenter(e);
  }
  function _onmouseleave(e) {
    hovered = false;
    onmouseleave(e);
  }

  function _onclick(e) {
    onclick(e);
  }

  let iconColor = $derived(getIconColor(variant, hovered, el));
</script>

<button
  class="variant-{variant} size-{size}"
  bind:this={el}
  {disabled}
  aria-disabled={disabled}
  onmouseenter={_onmouseenter}
  onmouseleave={_onmouseleave}
  onclick={_onclick}
>
  {#if iconPosition == "left"}
    <span class="button-icon button-left">
      {@render icon(iconColor)}
    </span>
  {:else}
    <div style:width={"16px"}></div>
  {/if}
  {@render children()}
  {#if iconPosition == "right"}
    <span class="button-icon button-right">
      {@render icon(iconColor)}
    </span>
  {:else}
    <div style:width={"16px"}></div>
  {/if}
</button>

<style>
  button {
    --border-size: 2px;
    --button-color: var(--color-white);
    appearance: none;
    border: none;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    /* gap: 30px; */
    max-width: 100%;
    width: 324px;
    color: var(--button-color);
    font-family: var(--font-family-sans);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    /* text-transform: uppercase; */
  }

  button:disabled {
    cursor: not-allowed;
  }

  .button-icon.button-right {
    display: inline-block;
    /* margin-left: 30px; */
  }

  .button-icon.button-left {
    display: inline-block;
    /* margin-right: 30px; */
  }

  /* sizes */
  button.size-standard {
    padding: calc(1rem - var(--border-size) * 2);
  }

  button.size-small {
    padding: calc(0.75rem - var(--border-size) * 2);
  }

  /* primary variant */
  button.variant-primary {
    --button-color: var(--color-white);
    background-color: var(--color-blue);
    border: solid var(--border-size) var(--color-blue);
  }

  button.variant-primary:hover:not(:disabled) {
    background-color: var(--color-blue-shade-dark);
    border: solid var(--border-size) var(--color-blue-shade-dark);
  }

  /* primary-black variant */
  button.variant-primary-black {
    --button-color: var(--color-white);
    background-color: var(--color-black);
    border: solid var(--border-size) var(--color-black);
  }

  button.variant-primary-black:hover {
    --button-color: var(--color-black);
    background-color: var(--color-white);
    border: solid var(--border-size) var(--color-black);
  }

  /* primary-gray variant */
  button.variant-primary-gray {
    --button-color: var(--color-black);
    background-color: var(--color-gray-shade-light);
    border: solid var(--border-size) var(--color-gray-shade-light);
  }

  button.variant-primary-gray:hover {
    --button-color: var(--color-black);
    background-color: var(--color-gray);
    border: solid var(--border-size) var(--color-gray);
  }

  /* secondary variant */
  button.variant-secondary {
    --button-color: var(--color-blue);
    background-color: var(--color-white);
    border: solid var(--border-size) var(--color-blue);
  }

  button.variant-secondary:hover:not(:disabled) {
    --button-color: var(--color-white);
    background-color: var(--color-blue);
    border: solid var(--border-size) var(--color-blue);
  }

  button.variant-secondary:disabled,
  button.variant-secondary-black:disabled {
    --button-color: var(--color-gray);
    border: solid var(--border-size) var(--color-gray);
  }

  /* secondary-black variant */
  button.variant-secondary-black {
    --button-color: var(--color-black);
    background-color: var(--color-white);
    border: solid var(--border-size) var(--color-black);
  }

  button.variant-secondary-black:hover:not(:disabled) {
    --button-color: var(--color-white);
    background-color: var(--color-black);
    border: solid var(--border-size) var(--color-black);
  }

  /* tertiary variant */
  button.variant-tertiary {
    --button-color: var(--color-black);
    background-color: var(--color-yellow);
    border: solid var(--border-size) var(--color-yellow);
  }

  button.variant-tertiary:hover {
    --button-color: var(--color-white);
    background-color: var(--color-black);
    border: solid var(--border-size) var(--color-black);
  }

  /* disabled state */
  button.variant-primary:disabled,
  button.variant-primary-black:disabled,
  button.variant-tertiary:disabled {
    --button-color: var(--color-white);
    background-color: var(--color-gray);
    border: solid var(--border-size) var(--color-gray);
  }

  button.variant-secondary:disabled,
  button.variant-secondary-black:disabled {
    --button-color: var(--color-gray);
    border: solid var(--border-size) var(--color-gray);
  }
</style>
